<template>
	<section class="cooperative-media" :aria-label="t(`${I18N_NS}.cooperativeMedia.ariaLabel`)">
		<header class="cooperative-media__header">
			<div class="cooperative-media__title-row">
				<img
					class="cooperative-media__decor"
					src="../../../pc/images/title-decor-left.png"
					alt=""
					aria-hidden="true"
				/>
				<h2 class="cooperative-media__title">{{ t(`${I18N_NS}.cooperativeMedia.title`) }}</h2>
				<img
					class="cooperative-media__decor"
					src="../../../pc/images/title-decor-right.png"
					alt=""
					aria-hidden="true"
				/>
			</div>
			<p class="cooperative-media__subtitle">{{ t(`${I18N_NS}.cooperativeMedia.subtitle`) }}</p>
		</header>

		<div class="cooperative-media__group-list">
			<section
				v-for="(group, index) in logoGroups"
				:key="`${group.title}-${index}`"
				class="cooperative-media__group"
			>
				<div class="cooperative-media__group-info">
					<img class="cooperative-media__group-icon" :src="group.icon" alt="" aria-hidden="true" />
					<div class="cooperative-media__group-text">
						<h3 class="cooperative-media__group-title">{{ group.title }}</h3>
						<p class="cooperative-media__group-desc">{{ group.subtitle }}</p>
					</div>
				</div>

				<div class="cooperative-media__logo-list">
					<div
						v-for="logo in group.logos"
						:key="logo.key"
						:ref="el => setLogoRef(el, logo)"
						class="cooperative-media__logo-item"
					>
						<img :src="logo.src" :alt="logo.alt" class="cooperative-media__logo-image" />
					</div>
				</div>
			</section>
		</div>
	</section>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted } from "vue";
import iconTv from "../../../pc/components/cooperative-media-section/images/tv.png";
import iconPlatform from "../../../pc/components/cooperative-media-section/images/platform.png";
import iconChannel from "../../../pc/components/cooperative-media-section/images/channel.png";
import iconMedia from "../../../pc/components/cooperative-media-section/images/media.png";
import { useI18n } from "~/composables/useI18n";
import { useAboutUsTracking } from "../../../composables/useAboutUsTracking";

const I18N_NS = "aboutUs";
const { t, trackTranslationDeps } = useI18n();
const { track } = useAboutUsTracking("mobile");

const logoRefs = new Map();
const impressedLogoKeys = new Set();
const impressionTimers = new Map();
let logoObserver = null;

const GROUP_ICONS = [iconTv, iconPlatform, iconChannel, iconMedia];
const GROUP_LOGO_CONFIG = [
	{ prefix: "tv", count: 4 },
	{ prefix: "platform", count: 2 },
	{ prefix: "channel", count: 5 },
	{ prefix: "media", count: 19, excludedIndexes: new Set([1, 2, 3, 4, 5, 8, 14, 15, 19]) },
];
const PARTNER_NAME_MAP = {
	tv1: "Astro",
	tv2: "TVB",
	tv3: "Singtel",
	tv4: "Metro 新城",
	platform1: "myTV SUPER",
	platform2: "WeTV",
	channel1: "YouTube",
	channel2: "Facebook",
	channel3: "Instagram",
	channel4: "WhatsApp",
	channel5: "TikTok",
	media6: "光华日报",
	media7: "生活",
	media9: "NEWSBEE",
	media10: "卓越雜誌",
	media11: "Melody",
	media12: "中國報",
	media13: "星洲日報",
	media16: "南洋商報",
	media17: "東方日報",
	media18: "Xuan",
};

const logoModules = import.meta.glob("../../../images/*.{png,jpg,jpeg,webp,svg}", {
	eager: true,
	import: "default",
});

const getLogoSrc = name => {
	const filePath = Object.keys(logoModules).find(key => key.endsWith(`/images/${name}.png`));
	return filePath ? logoModules[filePath] : "";
};

const createLogoList = (prefix, count, excludedIndexes = new Set()) =>
	Array.from({ length: count }, (_, index) => {
		if (excludedIndexes.has(index + 1)) {
			return null;
		}

		const name = `${prefix}${index + 1}`;
		const partnerName = PARTNER_NAME_MAP[name] || name;
		return {
			key: name,
			src: getLogoSrc(name),
			alt: partnerName,
			partnerName,
		};
	}).filter(item => item?.src);

const logoGroups = computed(() => {
	trackTranslationDeps();
	return GROUP_LOGO_CONFIG.map((config, index) => ({
		title: t(`${I18N_NS}.cooperativeMedia.groups.${index}.title`),
		subtitle: t(`${I18N_NS}.cooperativeMedia.groups.${index}.subtitle`),
		icon: GROUP_ICONS[index],
		logos: createLogoList(config.prefix, config.count, config.excludedIndexes || new Set()),
	}));
});

const flatLogoList = computed(() => {
	const items = [];
	logoGroups.value.forEach(group => {
		group.logos.forEach(logo => {
			items.push({
				...logo,
				partner_type: group.title,
			});
		});
	});
	return items.map((logo, index) => ({
		...logo,
		position: index + 1,
	}));
});

const setLogoRef = (el, logo) => {
	if (!logo?.key) return;
	if (el) {
		logoRefs.set(logo.key, el);
	} else {
		logoRefs.delete(logo.key);
	}
};

function refreshLogoObserver() {
	if (!import.meta.client) return;
	logoObserver?.disconnect();

	logoObserver = new IntersectionObserver(
		entries => {
			entries.forEach(entry => {
				const key = entry.target.dataset.logoKey;
				if (!key || impressedLogoKeys.has(key)) return;

				if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
					if (impressionTimers.has(key)) return;
					const timer = window.setTimeout(() => {
						const logo = flatLogoList.value.find(item => item.key === key);
						if (!logo || impressedLogoKeys.has(key)) return;
						impressedLogoKeys.add(key);
						track("media_partner_logo_impression", {
							partner_type: logo.partner_type,
							partner_name: logo.partnerName,
							position: logo.position,
						});
					}, 1000);
					impressionTimers.set(key, timer);
				} else if (impressionTimers.has(key)) {
					window.clearTimeout(impressionTimers.get(key));
					impressionTimers.delete(key);
				}
			});
		},
		{ threshold: [0.5] }
	);

	flatLogoList.value.forEach(logo => {
		const el = logoRefs.get(logo.key);
		if (!el) return;
		el.dataset.logoKey = logo.key;
		logoObserver.observe(el);
	});
}

onMounted(() => {
	nextTick(refreshLogoObserver);
});

onBeforeUnmount(() => {
	impressionTimers.forEach(timer => window.clearTimeout(timer));
	impressionTimers.clear();
	logoObserver?.disconnect();
});
</script>

<style lang="scss" scoped>
.cooperative-media {
	padding: 0 32px 48px;
	background: #ffffff;

	&__header {
		text-align: center;
	}

	&__title-row {
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 12px;
		margin-bottom: 4px;
	}

	&__decor {
		width: 94px;
		height: 12px;
		object-fit: contain;
		flex-shrink: 0;
	}

	&__title {
		margin: 0;
		color: #333333;
		font-size: 34px;
		font-weight: 700;
		line-height: 48px;
		white-space: nowrap;
	}

	&__subtitle {
		margin: 0 0 48px;
		color: #333333;
		font-size: 24px;
		line-height: 34px;
		text-align: center;
	}

	&__group-list {
		display: flex;
		flex-direction: column;
		gap: 30px;
	}

	&__group {
		padding: 0;
		background: #ffffff;
	}

	&__group-info {
		display: flex;
		align-items: center;
		gap: 16px;
		margin-bottom: 20px;
	}

	&__group-icon {
		width: 68px;
		height: 68px;
		flex-shrink: 0;
		object-fit: contain;
	}

	&__group-text {
		min-width: 0;
	}

	&__group-title {
		margin: 0 0 6px;
		color: #333333;
		font-size: 28px;
		font-weight: bold;
		line-height: 1.4;
	}

	&__group-desc {
		margin: 0;
		color: #a0a0a0;
		font-size: 22px;
		line-height: 1.5;
	}

	&__logo-list {
		display: grid;
		grid-template-columns: repeat(3, minmax(0, 1fr));
		gap: 24px 30px;
	}

	// &__logo-item {
	// 	display: flex;
	// 	align-items: center;
	// 	justify-content: center;
	// 	// min-height: 96px;
	// 	// box-sizing: border-box;
	// }

	&__logo-image {
		display: block;
		width: 100%;
		max-height: 100px;
		object-fit: contain;
	}
}
</style>
